import {
  PublicClientApplication,
  type SilentRequest,
  type PopupRequest,
  type AccountInfo,
  type AuthenticationResult,
} from "@azure/msal-browser";
import type { AccountFilter, BaseAuthRequest } from "@azure/msal-common";
import type { AzureADConfig } from "~/types/auth";

const azureConfig = ref<AzureADConfig>();
const azureADInstance = ref<PublicClientApplication>();

export function useMSAL() {
  const runtimeConfig = useRuntimeConfig();

  const msalInitialized = ref(false);
  const msalToken = ref<string | null>(null);
  const redirectUrl = import.meta.client
    ? (useBrowserLocation()?.value?.origin ?? runtimeConfig.public.siteUrl)
    : runtimeConfig.public.siteUrl;
  azureConfig.value = {
    clientId: runtimeConfig.public.azureADClientId,
    authority: `https://login.microsoftonline.com/${runtimeConfig.public.azureADTenantId}/`,
    redirectUrl,
  };
  async function initializeAzureAD() {
    if (!azureConfig.value) return;
    azureADInstance.value = new PublicClientApplication({
      auth: azureConfig.value,
    });
    await azureADInstance.value.initialize();
    azureADInstance.value
      ?.handleRedirectPromise()
      .then((response) => {
        if (response) handleSuccessfulRedirect(response);
      })
      .catch((error) => {
        doNothing(error);
      });
    msalInitialized.value = true;
  }

  async function handleSuccessfulRedirect(result: AuthenticationResult) {
    const route = useRoute();
    msalToken.value = result.accessToken;
    currentAccount.value = result.account;
    if (route.name !== "index") await navigateTo({ name: "index" });
  }

  async function login() {
    if (!msalInitialized.value) await initializeAzureAD();
    azureADInstance.value?.loginRedirect({
      redirectUri: redirectUrl,
      scopes: ["user.read", "mail.send"],
    });
  }

  async function logout() {
    try {
      await getAccounts();
      if (!currentAccount.value) return;
      await azureADInstance.value?.logoutPopup({
        authority: azureConfig.value?.authority,
      });
    } catch (e) {
      doNothing(e);
    }

    msalInitialized.value = false;
    msalToken.value = null;
  }

  const availableAccounts: Ref<AccountInfo[]> = ref([]);
  const currentAccount = ref<AccountInfo>();

  async function getAccounts() {
    if (!msalInitialized.value) await initializeAzureAD();
    availableAccounts.value = azureADInstance.value?.getAllAccounts() ?? [];
    if (!availableAccounts.value || availableAccounts.value.length < 1) {
      // login();
    } else if (availableAccounts.value.length > 1) {
      // Add choose account code here
      currentAccount.value = availableAccounts.value[0];
    } else if (availableAccounts.value.length === 1) {
      currentAccount.value = availableAccounts.value[0];
    }
  }

  async function findAccount(
    accountFilter: Partial<AccountFilter>,
  ): Promise<AccountInfo | null> {
    if (!msalInitialized.value) await initializeAzureAD();
    return azureADInstance.value?.getAccount(accountFilter ?? {}) ?? null;
  }

  async function getToken(
    request: Partial<BaseAuthRequest> & Pick<BaseAuthRequest, "scopes">,
    accountFilter: Partial<AccountFilter>,
  ) {
    if (!msalInitialized.value) await initializeAzureAD();
    const silentRequest: SilentRequest = { ...request };
    silentRequest.account = (await findAccount(accountFilter)) ?? undefined;
    await azureADInstance.value
      ?.acquireTokenSilent(silentRequest)
      .then((authResult) => {
        msalToken.value = authResult.accessToken;
      })
      .catch(() => {
        return azureADInstance.value?.acquireTokenRedirect(
          request as PopupRequest,
        );
      });
  }

  return {
    azureConfig,
    azureADInstance: readonly(azureADInstance),
    msalToken,
    currentAccount,
    availableAccounts,
    initializeAzureAD,
    login,
    logout,
    getAccounts,
    getToken,
  };
}
